import { Button } from '@alkem/react-ui-button';
import { LazyTooltip } from '@alkem/react-ui-tooltip';
import classNames from 'classnames';
import { MouseEvent, PureComponent, ReactNode } from 'react';
import ReactModal from 'react-modal';
import './modal.scss';

// STYLES

const defaultStyle = {
  overlay: {
    zIndex: '1050',
    background: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    position: 'absolute',
    top: '17.5%',
    left: '17.5%',
    right: '17.5%',
    bottom: '25%',
    border: '0px',
    background: '#fff',
    overflow: 'auto',
    borderRadius: '0px',
    outline: 'none',
    padding: '0px',
    boxShadow: '0 3px 6px rgba(0,0,0,.16),0 3px 6px rgba(0,0,0,.23)',
  },
};

const dynamicStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  }),
});

const fullHeightStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    top: '5%',
    bottom: '10%',
  }),
});

const largeStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    top: '5%',
    left: '5%',
    right: '5%',
    bottom: '10%',
  }),
});

const HPPStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    top: '0px',
    bottom: '0px',
    background: '#f7f7f7',
  }),
});

const roundCornersStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    borderRadius: '10px',
    background: 'transparent',
  }),
});

const rightStyle = Object.assign({}, defaultStyle, {
  content: Object.assign({}, defaultStyle.content, {
    top: '0',
    left: '50%',
    right: '0',
    bottom: '0',
  }),
});

// TYPES

type ModalStyle =
  | 'default'
  | 'dynamic'
  | 'fullHeight'
  | 'hpp'
  | 'large'
  | 'roundCorner'
  | 'right';

type Action = (e?: MouseEvent) => void;

export interface Props {
  // required
  title: string;
  children: ReactNode;

  // optional
  testid?: string;
  confirmButtonText?: string; // default "OK"
  confirmButtonWarning?: boolean;
  confirmDisabled?: boolean;
  confirmTooltip?: ReactNode;
  closeButtonText?: string; // default "Cancel"
  shouldCloseOnOverlayClick?: boolean; // default "true"
  modalStyle?: ModalStyle;
  isProcessing?: boolean;
  danger?: boolean; // header and confirm button in red
  hideFooter?: boolean;
  additionalFooterContent?: ReactNode; // only on the left side of the buttons
  centerFooterContent?: ReactNode;
  className?: string;
  resizePics?: boolean; // TODO remove that
  hideCloseButton?: boolean;
  preventClose?: boolean;
  withTransition: boolean;
  secondaryAction?: [string, Action] | [string, Action, any];
  onConfirm?: Action;
  onClose?: Action;
  warning?: boolean;
}

// COMPONENT

class Modal extends PureComponent<Props> {
  public static defaultProps = {
    confirmButtonText: 'OK',
    closeButtonText: 'Cancel',
    modalStyle: 'default',
    withTransition: true,
    testid: 'Modal',
    onConfirm() {
      // noop
    },
  };

  public static styles = [
    'default',
    'dynamic',
    'fullHeight',
    'large',
    'hpp',
    'roundCorner',
    'right',
  ];

  private static styleMap = {
    default: defaultStyle,
    dynamic: dynamicStyle,
    fullHeight: fullHeightStyle,
    large: largeStyle,
    hpp: HPPStyle,
    roundCorner: roundCornersStyle,
    right: rightStyle,
  };

  private renderHeader = () => {
    const { danger, className, onClose, title, warning } = this.props;
    return (
      <div
        className={classNames('Modal__header', {
          'Modal__header--danger': danger,
          'Modal__header--warning': warning,
          [`${className}__header`]: !!className,
        })}
      >
        <h3 className="Modal__title">{title}</h3>
        {onClose && (
          <i className="Modal__close mdi mdi-close" onClick={onClose} />
        )}
      </div>
    );
  };

  private renderBody() {
    const { children, className, hideFooter, modalStyle, resizePics } =
      this.props;
    return (
      <div
        className={classNames('Modal__body', {
          'Modal__body--full': hideFooter,
          'Modal__body--dynamic': modalStyle === 'dynamic',
          'Modal__body--resizedPics': resizePics,
          [`${className}__body`]: !!className,
        })}
      >
        {children}
      </div>
    );
  }

  private renderFooter() {
    const {
      testid,
      additionalFooterContent,
      centerFooterContent,
      className,
      closeButtonText,
      confirmButtonText,
      confirmButtonWarning,
      confirmDisabled,
      confirmTooltip,
      danger,
      warning,
      hideCloseButton,
      isProcessing,
      modalStyle,
      onClose,
      onConfirm,
      secondaryAction,
    } = this.props;

    const withCenterContent = !!centerFooterContent;
    const withLeftContent = !!additionalFooterContent || withCenterContent;

    let confirmButton = (
      <Button
        testid={`${testid}-confirm`}
        primary={!confirmButtonWarning}
        warning={confirmButtonWarning}
        className={classNames('Modal__footerConfirmButton', {
          'Modal__footerConfirmButton--danger': danger,
          'Modal__footerConfirmButton--warning': warning,
        })}
        onClick={onConfirm}
        disabled={isProcessing || confirmDisabled}
        displaySpinner={isProcessing}
      >
        {confirmButtonText}
      </Button>
    );
    if (confirmTooltip) {
      confirmButton = (
        <LazyTooltip
          id="modal-confirm-button-tooltip"
          tooltipLabel={confirmTooltip}
          place="left"
        >
          <span>{confirmButton}</span>
        </LazyTooltip>
      );
    }

    return (
      <div
        className={classNames('Modal__footer', {
          'Modal__footer--dynamic': modalStyle === 'dynamic',
          [`${className}__footer`]: !!className,
        })}
      >
        {withLeftContent && (
          <div className="Modal__additionalFooterContent">
            {additionalFooterContent}
          </div>
        )}
        {withCenterContent && (
          <div className="Modal__centerFooterContent">
            {centerFooterContent}
          </div>
        )}
        <div className="Modal__mainFooterContent">
          {onClose && !hideCloseButton && (
            <Button
              testid={`${testid}-close`}
              link
              className="Modal__footerCloseButton"
              onClick={onClose}
            >
              {closeButtonText}
            </Button>
          )}
          {secondaryAction && (
            <Button
              testid={`${testid}-secondary-action`}
              className="Modal__footerSecondaryButton"
              onClick={secondaryAction[1]}
              secondary
              {...(secondaryAction[2] || {})}
            >
              {secondaryAction[0]}
            </Button>
          )}
          {confirmButton}
        </div>
      </div>
    );
  }

  public render() {
    const {
      testid,
      className = '',
      hideFooter,
      modalStyle = 'default',
      onClose,
      shouldCloseOnOverlayClick = true,
      withTransition,
    } = this.props;
    return (
      <ReactModal
        isOpen
        style={Modal.styleMap[modalStyle]}
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        ariaHideApp={false}
        portalClassName={classNames('ReactModalAdvanced', {
          ReactModalWithTransition: withTransition,
        })}
      >
        <div
          data-testid={testid}
          className={classNames('Modal', {
            [className]: !!className,
            'Modal--withoutFooter': hideFooter,
          })}
        >
          {this.renderHeader()}
          {this.renderBody()}
          {!hideFooter && this.renderFooter()}
        </div>
      </ReactModal>
    );
  }
}

export default Modal;
